import Navigation from "../components/Navigation";
import { useNavigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

const Finances = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Debug: Check if translations are loaded
  console.log("Current language:", i18n.language);
  console.log("Available languages:", i18n.languages);
  console.log("Test translation:", t("finances.title"));
  console.log(
    "All finances keys:",
    Object.keys(
      i18n.getResourceBundle(i18n.language, "translation")?.finances || {}
    )
  );

  // Scroll to top immediately when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleContactClick = () => {
    if (location.pathname === "/") {
      // On home page, scroll to contact section
      const element = document.querySelector("#contact");
      if (element) {
        const navHeight = 80;
        const elementPosition = element.offsetTop - navHeight;
        window.scrollTo({
          top: elementPosition,
          behavior: "smooth",
        });
        window.history.pushState(null, null, "#contact");
      }
    } else {
      // On other pages, navigate to home page with contact section
      navigate("/#contact");
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleContactClick();
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Skip link for keyboard users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:bg-[#C5A66A] focus:text-[#0F1B2D] focus:px-4 focus:py-2 focus:rounded focus:font-semibold"
      >
        Skip to main content
      </a>

      <Navigation />
      <main id="main-content" className="pt-32 pb-20">
        <div className="container mx-auto px-6 max-w-6xl">
          {/* Case Study */}
          <div className="mb-20">
            {/* Header */}
            <div className="mb-16">
              <h1 className="text-4xl font-bold text-[#0F1B2D] mb-4">
                {t("finances.title")}
              </h1>
              <h2 className="text-2xl font-semibold text-[#0F1B2D] mb-6">
                {t("finances.subtitle")}
              </h2>
            </div>

            {/* Hero Results */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8 mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-6">
                {t("finances.heroResults.title")}
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t("finances.heroResults.results.netWorth.value")}
                  </div>
                  <div className="text-gray-700">
                    {t("finances.heroResults.results.netWorth.description")}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t("finances.heroResults.results.liquidityReserve.value")}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "finances.heroResults.results.liquidityReserve.description"
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t("finances.heroResults.results.taxRate.value")}
                  </div>
                  <div className="text-gray-700">
                    {t("finances.heroResults.results.taxRate.description")}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t("finances.heroResults.results.officeEquity.value")}
                  </div>
                  <div className="text-gray-700">
                    {t("finances.heroResults.results.officeEquity.description")}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t("finances.heroResults.results.retirementCapital.value")}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "finances.heroResults.results.retirementCapital.description"
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t("finances.heroResults.results.structured.value")}
                  </div>
                  <div className="text-gray-700">
                    {t("finances.heroResults.results.structured.description")}
                  </div>
                </div>
              </div>
            </div>

            {/* Circumstance */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-4">
                {t("finances.circumstance.title")}
              </h3>
              <div className="bg-gray-50 rounded-xl p-6">
                <p className="text-lg text-gray-700 leading-relaxed">
                  {t("finances.circumstance.description")}
                </p>
              </div>
            </div>

            {/* Complication */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-4">
                {t("finances.complication.title")}
              </h3>
              <div className="space-y-4">
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-red-800 mb-2">
                    {t("finances.complication.issues.volatileCashflows.title")}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "finances.complication.issues.volatileCashflows.description"
                    )}
                  </p>
                </div>
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-red-800 mb-2">
                    {t("finances.complication.issues.highTaxBurden.title")}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "finances.complication.issues.highTaxBurden.description"
                    )}
                  </p>
                </div>
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-red-800 mb-2">
                    {t("finances.complication.issues.noRealEstateEquity.title")}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "finances.complication.issues.noRealEstateEquity.description"
                    )}
                  </p>
                </div>
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-red-800 mb-2">
                    {t("finances.complication.issues.unclearRetirement.title")}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "finances.complication.issues.unclearRetirement.description"
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Catalyst */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-4">
                {t("finances.catalyst.title")}
              </h3>
              <div className="bg-blue-50 rounded-xl p-6">
                <p className="text-lg text-gray-700 mb-6">
                  {t("finances.catalyst.description")}
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-lg">
                    <thead className="bg-[#0F1B2D] text-white">
                      <tr>
                        <th className="px-6 py-4 text-left font-semibold">
                          {t("finances.catalyst.table.headers.component")}
                        </th>
                        <th className="px-6 py-4 text-left font-semibold">
                          {t("finances.catalyst.table.headers.result")}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-medium">
                          {t(
                            "finances.catalyst.table.rows.financialInventory.component"
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {t(
                            "finances.catalyst.table.rows.financialInventory.result"
                          )}
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-medium">
                          {t(
                            "finances.catalyst.table.rows.liquidityPlan.component"
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {t(
                            "finances.catalyst.table.rows.liquidityPlan.result"
                          )}
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-medium">
                          {t(
                            "finances.catalyst.table.rows.taxOptimization.component"
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {t(
                            "finances.catalyst.table.rows.taxOptimization.result"
                          )}
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-medium">
                          {t(
                            "finances.catalyst.table.rows.investmentPlan.component"
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {t(
                            "finances.catalyst.table.rows.investmentPlan.result"
                          )}
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-medium">
                          {t(
                            "finances.catalyst.table.rows.realEstateModel.component"
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {t(
                            "finances.catalyst.table.rows.realEstateModel.result"
                          )}
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-medium">
                          {t(
                            "finances.catalyst.table.rows.monitoring.component"
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {t("finances.catalyst.table.rows.monitoring.result")}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Planungs-Highlights */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-4">
                {t("finances.planningHighlights.title")}
              </h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                  <div>
                    <span className="font-semibold">
                      {t(
                        "finances.planningHighlights.highlights.cashflowBuffer.title"
                      )}
                    </span>{" "}
                    {t(
                      "finances.planningHighlights.highlights.cashflowBuffer.description"
                    )}
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                  <div>
                    <span className="font-semibold">
                      {t(
                        "finances.planningHighlights.highlights.taxLeverage.title"
                      )}
                    </span>{" "}
                    {t(
                      "finances.planningHighlights.highlights.taxLeverage.description"
                    )}
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                  <div>
                    <span className="font-semibold">
                      {t(
                        "finances.planningHighlights.highlights.automatedETF.title"
                      )}
                    </span>{" "}
                    {t(
                      "finances.planningHighlights.highlights.automatedETF.description"
                    )}
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                  <div>
                    <span className="font-semibold">
                      {t(
                        "finances.planningHighlights.highlights.quarterlyTracking.title"
                      )}
                    </span>{" "}
                    {t(
                      "finances.planningHighlights.highlights.quarterlyTracking.description"
                    )}
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                  <div>
                    <span className="font-semibold">
                      {t(
                        "finances.planningHighlights.highlights.singleContact.title"
                      )}
                    </span>{" "}
                    {t(
                      "finances.planningHighlights.highlights.singleContact.description"
                    )}
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                  <div>
                    <span className="font-semibold">
                      {t(
                        "finances.planningHighlights.highlights.lowCodeApproach.title"
                      )}
                    </span>{" "}
                    {t(
                      "finances.planningHighlights.highlights.lowCodeApproach.description"
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Impact */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-6">
                {t("finances.impact.title")}
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-lg">
                  <thead className="bg-[#0F1B2D] text-white">
                    <tr>
                      <th className="px-6 py-4 text-left font-semibold">
                        {t("finances.impact.table.headers.kpi")}
                      </th>
                      <th className="px-6 py-4 text-center font-semibold">
                        {t("finances.impact.table.headers.before")}
                      </th>
                      <th className="px-6 py-4 text-center font-semibold">
                        {t("finances.impact.table.headers.after")}
                      </th>
                      <th className="px-6 py-4 text-center font-semibold">
                        {t("finances.impact.table.headers.delta")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t("finances.impact.table.rows.netWorth.kpi")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t("finances.impact.table.rows.netWorth.before")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t("finances.impact.table.rows.netWorth.after")}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t("finances.impact.table.rows.netWorth.delta")}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t("finances.impact.table.rows.liquidityReserve.kpi")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "finances.impact.table.rows.liquidityReserve.before"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t("finances.impact.table.rows.liquidityReserve.after")}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t("finances.impact.table.rows.liquidityReserve.delta")}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t("finances.impact.table.rows.taxRate.kpi")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t("finances.impact.table.rows.taxRate.before")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t("finances.impact.table.rows.taxRate.after")}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t("finances.impact.table.rows.taxRate.delta")}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t("finances.impact.table.rows.officeEquity.kpi")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t("finances.impact.table.rows.officeEquity.before")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t("finances.impact.table.rows.officeEquity.after")}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t("finances.impact.table.rows.officeEquity.delta")}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t("finances.impact.table.rows.retirementCapital.kpi")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "finances.impact.table.rows.retirementCapital.before"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "finances.impact.table.rows.retirementCapital.after"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t(
                          "finances.impact.table.rows.retirementCapital.delta"
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Success Factors */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-4">
                {t("finances.successFactors.title")}
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-green-800 mb-2">
                    {t("finances.successFactors.factors.cashflowFirst.title")}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "finances.successFactors.factors.cashflowFirst.description"
                    )}
                  </p>
                </div>
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-green-800 mb-2">
                    {t(
                      "finances.successFactors.factors.taxLeverageActivated.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "finances.successFactors.factors.taxLeverageActivated.description"
                    )}
                  </p>
                </div>
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-green-800 mb-2">
                    {t("finances.successFactors.factors.disciplinedETF.title")}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "finances.successFactors.factors.disciplinedETF.description"
                    )}
                  </p>
                </div>
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-green-800 mb-2">
                    {t(
                      "finances.successFactors.factors.quarterlyMonitoring.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "finances.successFactors.factors.quarterlyMonitoring.description"
                    )}
                  </p>
                </div>
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl md:col-span-2">
                  <div className="font-semibold text-green-800 mb-2">
                    {t(
                      "finances.successFactors.factors.coordinatedAdvice.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "finances.successFactors.factors.coordinatedAdvice.description"
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Customer Quote */}
            <div className="mb-12">
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-8">
                <div className="flex items-start space-x-4">
                  <div className="text-4xl text-indigo-600">"</div>
                  <div>
                    <p className="text-lg text-gray-700 italic mb-4">
                      {t("finances.customerQuote.quote")}
                    </p>
                    <div className="font-semibold text-[#0F1B2D]">
                      {t("finances.customerQuote.author")}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-[#0F1B2D] to-[#1a2d4a] rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-4 text-[#C5A66A]">
                {t("finances.callToAction.title")}
              </h3>
              <p className="text-lg mb-6">
                {t("finances.callToAction.description")}
              </p>
              <button
                onClick={handleContactClick}
                onKeyDown={handleKeyDown}
                className="bg-[#C5A66A] text-[#0F1B2D] px-8 py-3 rounded-lg font-semibold hover:bg-[#b8966b] transition-all duration-300 transform hover:scale-105"
              >
                {t("finances.callToAction.button")}
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Finances;
